import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import InvoiceForm from '../components/invoices/InvoiceForm';
import StockistInvoiceForm from '../components/invoices/StockistInvoiceForm';
import StockistEditForm from '../components/invoices/StockistEditForm';
import {
  addInvoice,
  applyStockDeltas,
  createStockistInvoice,
  listInvoices,
  updateInvoice,
} from '../lib/invoices';
import {
  addStockist,
  listStatements,
  listStockists,
  sentDeltas,
  updateStockistInvoice,
} from '../lib/stockist';
import { listProducts } from '../lib/products';
import { useAuth } from '../context/AuthContext';
import './Invoices.css';

// Creating an invoice, on its own route rather than inline above the list.
//
// The form is long — line items, a product picker, a stockist profile — and
// pushing the list down the page every time it opened made the section feel
// like it was *for* data entry. Its own page also means Back works and the
// URL can be shared.
export default function InvoiceNew({ kind = 'Direct', mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const stockist = kind === 'Stockist';
  const editing = mode === 'edit';

  const [products, setProducts] = useState([]);
  const [stockists, setStockists] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [statementsByInvoice, setStatements] = useState({});
  const [existing, setExisting] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const needsInvoices = stockist || editing;
      const [productRows, stockistRows, invoiceRows] = await Promise.all([
        listProducts(),
        stockist ? listStockists() : Promise.resolve([]),
        needsInvoices ? listInvoices() : Promise.resolve([]),
      ]);
      setProducts(productRows);
      setStockists(stockistRows);
      setInvoices(invoiceRows);
      if (editing) setExisting(invoiceRows.find((inv) => inv.id === id) || null);
      // Statements are needed when editing too — they're what decides which
      // lines are locked and how far a quantity may be reduced.
      if (stockist || editing) {
        const ids = invoiceRows.filter((i) => i.kind === 'Stockist').map((i) => i.id);
        setStatements(await listStatements(ids));
      }
    } catch (err) {
      setError(err.message);
    }
  }, [stockist, editing, id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateStockist = async (fields) => {
    const created = await addStockist({ ...fields, created_by: user.id });
    setStockists((list) => [...list, created].sort((a, b) => a.name.localeCompare(b.name)));
    return created;
  };

  const handleDirect = async (fields, items) => {
    if (editing) {
      await updateInvoice(id, fields, items);
      navigate(`/invoices/${id}`);
      return;
    }
    const created = await addInvoice({ ...fields, created_by: user.id, kind: 'Direct' }, items);
    navigate(`/invoices/${created.id}`);
  };

  // Editing a consignment. The lines are updated in place (never replaced —
  // statement lines point at their ids), and your own stock moves by whatever
  // the quantities changed by, since the original send already took it off.
  const handleStockistEdit = async (fields, lines) => {
    const deltas = sentDeltas(existing.invoice_items || [], lines);
    await updateStockistInvoice(id, fields, lines);
    if (deltas.length) {
      try {
        await applyStockDeltas(deltas, products);
      } catch (err) {
        setError(`Saved, but stock counts didn't update: ${err.message}`);
      }
    }
    navigate(`/invoices/${id}`);
  };

  const handleStockist = async (fields, items) => {
    const result = await createStockistInvoice({ ...fields, created_by: user.id }, items, products);
    // The stock write is contained, so a failure there still leaves a valid
    // consignment — say so rather than pretending it all worked.
    if (result.stockError) {
      setError(`Consignment created, but stock counts didn't update: ${result.stockError}`);
    }
    navigate(`/invoices/${result.invoice.id}`);
  };

  return (
    <>
      <PageHeader
        title={editing ? `Edit invoice ${existing?.invoice_number || ''}` : stockist ? 'New stockist invoice' : 'New invoice'}
        subtitle={
          editing
            ? existing?.kind === 'Stockist'
              ? 'Correct what was sent. Quantities already sold or returned can’t be undone here.'
              : 'Change the customer, the lines, or the dates.'
            : stockist
              ? 'Send stock to a shop. Nothing is income until they report sales and pay for them.'
              : 'Bill a customer for goods sold outright.'
        }
        actions={
          <button
            className="ghost-button"
            onClick={() => navigate(editing ? `/invoices/${id}` : '/invoices')}
          >
            ← Back
          </button>
        }
      />

      {error && <p className="po-error">{error}</p>}

      <section className="panel">
        {editing && existing?.kind === 'Stockist' ? (
          <StockistEditForm
            invoice={existing}
            statements={statementsByInvoice[existing.id] || []}
            products={products}
            onSubmit={handleStockistEdit}
            onCancel={() => navigate(`/invoices/${id}`)}
          />
        ) : editing && !existing ? (
          <p className="po-loading">Loading…</p>
        ) : stockist ? (
          <StockistInvoiceForm
            products={products}
            stockists={stockists}
            invoices={invoices}
            statementsByInvoice={statementsByInvoice}
            onCreateStockist={handleCreateStockist}
            onSubmit={handleStockist}
            onCancel={() => navigate('/invoices')}
          />
        ) : (
          <InvoiceForm
            initialInvoice={existing}
            products={products}
            onSubmit={handleDirect}
            onCancel={() => navigate(editing ? `/invoices/${id}` : '/invoices')}
          />
        )}
      </section>
    </>
  );
}
